import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Prayer } from '@/types/prayer';
import {
  retryFailedTranscription,
  discardFailedAudio,
} from '@/lib/audio-queue';
import { enhancedVoiceAllowed } from '@/lib/core-cutover';
import { useAudioQueueStore, type RecordingAttention } from '@/lib/audio-queue-store';
import styles from './TranscriptionRecoveryBanner.module.css';

/**
 * V.B.2 recovery surface. Mounted by PrayerDetail above the raw-prayer block
 * for a terminal failure, or for any pending audio while Enhanced Voice is
 * disabled.
 * Terminal failures can expose three actions; a row parked behind the
 * disabled Enhanced Voice gate exposes only the non-destructive Type path:
 *
 *   - Type what you remember → flips parent's editingRaw state via
 *     onTypeMode callback. Banner stays mounted while user types so
 *     they can change their mind; it auto-unmounts when the parent's
 *     prayer prop reflects cleared transcriptionFailed (handled by
 *     the conditional render in PrayerDetail).
 *
 *   - Retry transcription → calls retryFailedTranscription. Banner
 *     shows "Retrying…" while awaiting; on success the prayer's
 *     transcriptionFailed flag clears via routeTranscribedText and
 *     the banner unmounts; on failure surfaces the inline error and
 *     returns to menu so the user can pick another action.
 *
 *   - Discard audio → only while Enhanced Voice is enabled; in-place expand
 *     to confirm, then discardFailedAudio. Inline error if the helper rejects
 *     (rare — row mismatch guard).
 *
 * LC4 (2026-10-19): `attention` is set for a pending recording the queue run
 * cannot process in this page, and replaces the finishing indicator with the
 * reason. `mismatch` (its saved bytes do not match the size and fingerprint
 * recorded when it was first saved, so it is never sent): only Type what you remember, which
 * keeps the recording (Retry would fail again; Remove's second local write is
 * refused in WebKit once the first has made the bytes unreadable in the page).
 * `unreadable` (its bytes cannot be read in this window): no action; the
 * person is asked to close and reopen the app so it can finish. `missing`
 * (LC7: no recording row for it on this device): only Type what you remember,
 * through the missing-row rescue of `parkTypedRecoveryAudio`, and (LC8) Remove
 * while Enhanced Voice is on, where discardFailedAudio's missing-row path works.
 *
 * Banner does not own any flag-clearing logic itself; it delegates
 * to the audio-queue helpers (which handle crash-safety ordering)
 * or to the parent's handleEditRawText (which handles the typing
 * recovery path atomically).
 */
interface Props {
  prayer: Prayer;
  /** True when audio is retained behind the disabled Enhanced Voice gate. */
  parked?: boolean;
  /** LC4: why the queue run cannot process this pending recording in this page. */
  attention?: RecordingAttention;
  onTypeMode: () => void;
  /**
   * V.B.2.1: invoked after a successful Remove. Prayer is gone from
   * the store at this point; PrayerDetail will unmount on the next
   * parent re-render anyway, but the parent should explicitly clear
   * its detailId state via this callback so a stale id isn't left
   * dangling. Wired to PrayerDetail's onClose.
   */
  onDeleted: () => void;
}

type Mode = 'menu' | 'retrying' | 'confirmDiscard';

export default function TranscriptionRecoveryBanner({
  prayer,
  parked = false,
  attention,
  onTypeMode,
  onDeleted,
}: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('menu');
  const [error, setError] = useState<string | null>(null);
  const canRetry = enhancedVoiceAllowed() && prayer.transcriptionFailed === true;
  const canDiscardAudioEntry = enhancedVoiceAllowed();
  // Presentation only. When the linked row already holds its transcript, the
  // words exist and could not be placed in this Prayer (a landing refusal, not
  // a provider failure): say so, and that nothing is lost.
  const item = useAudioQueueStore((s) => s.items.find((i) => i.id === prayer.pendingTranscriptionId));
  const landedWordsHeld =
    !parked && prayer.transcriptionFailed === true && Boolean(item?.transcribedText?.trim());

  const handleRetry = async () => {
    if (!enhancedVoiceAllowed()) return;
    setError(null);
    setMode('retrying');
    try {
      await retryFailedTranscription(prayer);
      // Banner unmounts when the parent re-renders with cleared
      // transcriptionFailed. If we're still mounted (race), reset
      // to menu defensively.
      setMode('menu');
    } catch (err) {
      console.error('[Adia] V.B.2 retry failed', err);
      const msg = err instanceof Error && err.message.includes('queue row not found')
        ? t('voice.recoveryAudioMissing')
        : t('voice.recoveryRetryFailed');
      setError(msg);
      setMode('menu');
    }
  };

  const handleDiscardConfirm = async () => {
    // While Enhanced Voice is shut, retained audio is preservation material.
    // Typing a replacement may commit the Prayer, but the exact audio row and
    // bytes remain parked; the recovery screen must not offer a delete-only
    // escape hatch.
    if (!enhancedVoiceAllowed()) return;
    setError(null);
    try {
      await discardFailedAudio(prayer);
      // V.B.2.1: Prayer is deleted by the helper. Call onDeleted before
      // any setState — the banner is about to unmount via parent
      // re-render (detailPrayer becomes null), and onDeleted explicitly
      // clears PrayPage's detailId so it doesn't carry a stale id.
      onDeleted();
    } catch (err) {
      console.error('[Adia] V.B.2.1 discard failed', err);
      // LC9: the stored prayer changed elsewhere (for example its words were
      // typed in another tab), so nothing was removed and trying again here
      // cannot remove it: say so instead of "Try again".
      setError(t(err instanceof Error && err.message === 'discardFailedAudio: prayer changed'
        ? 'voice.recoveryRemoveChanged'
        : 'voice.recoveryDeleteFailed'));
      setMode('menu');
    }
  };

  // The attention variants keep the existing confirm step for Remove (below).
  if (attention && mode !== 'confirmDiscard') {
    // LC7: `missing` (the recording row is not on this device) and `mismatch`
    // offer Type; `unreadable` offers no action. LC8: `missing` also offers
    // Remove where the existing removal really works: with Enhanced Voice on,
    // discardFailedAudio skips the missing row and removes the never-synced
    // prayer, failed or not; with it off that removal refuses, so it is absent.
    const keys = attention === 'mismatch'
      ? { title: 'voice.mismatchTitle', body: 'voice.mismatchBody' }
      : attention === 'missing'
        // The body names exactly the actions shown below.
        ? { title: 'voice.missingTitle', body: canDiscardAudioEntry ? 'voice.missingBodyRemove' : 'voice.missingBody' }
        : { title: 'voice.unreadableTitle', body: 'voice.unreadableBody' };
    return (
      <div className={styles.banner} role="status">
        <p className={styles.title}>{t(keys.title)}</p>
        <p className={styles.body}>{t(keys.body)}</p>
        {error && <p className={styles.error}>{error}</p>}
        {attention !== 'unreadable' && (
          <div className={styles.actionsCol}>
            <button
              type="button"
              className={styles.actionPrimary}
              onClick={onTypeMode}
            >
              {t('voice.recoveryActionType')}
            </button>
            {attention === 'missing' && canDiscardAudioEntry && (
              <button
                type="button"
                className={styles.actionTertiary}
                onClick={() => setMode('confirmDiscard')}
              >
                {t('voice.recoveryActionDiscard')}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (mode === 'retrying') {
    return (
      <div className={styles.banner} role="status" aria-live="polite">
        <span className={styles.retrySpinner} aria-hidden="true" />
        <span className={styles.retryingText}>{t('voice.recoveryRetrying')}</span>
      </div>
    );
  }

  if (mode === 'confirmDiscard') {
    return (
      <div className={styles.banner}>
        <p className={styles.title}>{t('voice.recoveryDiscardConfirm')}</p>
        <div className={styles.actionsRow}>
          <button
            type="button"
            className={styles.actionDanger}
            onClick={handleDiscardConfirm}
          >
            {t('voice.recoveryDiscardConfirmAction')}
          </button>
          <button
            type="button"
            className={styles.actionCancel}
            onClick={() => setMode('menu')}
          >
            {t('voice.recoveryDiscardCancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.banner}>
      <p className={styles.title}>
        {t(
          parked
            ? 'voice.recoveryParkedTitle'
            : landedWordsHeld
              ? 'voice.recoveryLandingTitle'
              : 'voice.recoveryBannerTitle',
        )}
      </p>
      <p className={styles.body}>
        {t(
          parked
            ? 'voice.recoveryParkedBody'
            : landedWordsHeld
              ? 'voice.recoveryLandingBody'
              : canRetry
                ? 'voice.recoveryBannerBody'
                : 'detail.recoveryFailed',
        )}
      </p>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.actionsCol}>
        <button
          type="button"
          className={styles.actionPrimary}
          onClick={onTypeMode}
        >
          {t('voice.recoveryActionType')}
        </button>
        {canRetry && (
          <button
            type="button"
            className={styles.actionSecondary}
            onClick={handleRetry}
          >
            {t('voice.recoveryActionRetry')}
          </button>
        )}
        {canDiscardAudioEntry && (
          <button
            type="button"
            className={styles.actionTertiary}
            onClick={() => setMode('confirmDiscard')}
          >
            {t('voice.recoveryActionDiscard')}
          </button>
        )}
      </div>
    </div>
  );
}
